import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { dealAction as dealActionApi, downloadContract, fetchDeal, releaseMilestone, startThread } from "./services/brandModeServices";
import { EMPTY_VALUE, fmtMoney, fullName } from "./brandUtils";
import { DELIVERABLE_STATUS, MILESTONE_STATUS, fmtDate, fmtDateRange, fmtDateTime } from "./brandDealUtils";
import iconInsight from "../../assets/brand/icon-insight.svg";
import { BackLink, Badge, Btn, Card, ErrorState, Loading, Overline, StatusBadge } from "./ui/BrandUI";

const DEAL_STEP = { offer_sent: 1, countered: 1, accepted: 2, contract_sent: 2, signed: 3, completed: 4, declined: 1, cancelled: 1, draft: 1 };
const STEPS = ["Open", "In Contract", "In Progress", "Completed"];

const Signature = ({ who, label, at, name }) => (
  <div className="flex flex-1 flex-col gap-1 rounded-[8px] bg-bm-subtle p-4">
    <span className="bm-body-s text-bm-muted">
      {name} ({who})
    </span>
    <span className="flex items-center gap-2 bm-label-l text-bm-ink">
      <span className="h-2 w-2 rounded-full" style={{ background: at ? "#1f9d55" : "#edd200" }} />
      {at ? `✓ Signed · ${fmtDateTime(at)}` : `${label} · pending`}
    </span>
  </div>
);

const Brief = ({ label, value }) => (
  <div className="flex flex-col gap-[2px]">
    <span className="bm-body-s text-bm-muted">{label}</span>
    <span className="bm-body-m text-bm-ink">{value || EMPTY_VALUE}</span>
  </div>
);

const DealTrackingPage = () => {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const { refreshBadges, refreshCampaigns } = useBrandMode();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const payload = await fetchDeal(dealId);
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load the deal");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const act = async (action, payload, success) => {
    setBusy(action);
    try {
      await dealActionApi(dealId, action, payload);
      if (success) toast.success(success);
      await load();
      refreshBadges();
      refreshCampaigns();
    } catch (err) {
      toast.error(err.message || "Could not update the deal");
    } finally {
      setBusy(null);
    }
  };

  const release = async (ms) => {
    setBusy(`release-${ms.id}`);
    try {
      await releaseMilestone(dealId, ms.id);
      toast.success(`${fmtMoney(ms.amount)} released.`);
      await load();
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not release the payout");
    } finally {
      setBusy(null);
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Contract files must be under 3 MB.");
    const reader = new FileReader();
    reader.onload = () => act("contract-upload", { fileName: f.name, dataUrl: reader.result }, `Uploaded ${f.name}.`);
    reader.readAsDataURL(f);
    return null;
  };

  const download = async () => {
    try {
      await downloadContract(dealId);
    } catch (err) {
      toast.error(err.message || "Could not download the contract");
    }
  };

  const message = async () => {
    try {
      const thread = await startThread(data.deal.campaignId, data.deal.athleteId);
      navigate(`/brand/campaigns/${data.deal.campaignId}/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  if (error) return <div className="py-10"><ErrorState>{error}</ErrorState></div>;
  if (!data) return <Loading label="Loading deal..." />;

  const { deal, campaign, brand } = data;
  const offer = deal.offer || {};
  const contract = deal.contract;
  const athleteName = fullName(deal.athlete);
  const signed = deal.status === "signed" || deal.status === "completed";
  const step = DEAL_STEP[deal.status] || 1;
  const signatures = (contract?.brandSignedAt ? 1 : 0) + (contract?.athleteSignedAt ? 1 : 0);
  const platforms = Array.from(new Set((offer.deliverables || []).map((d) => d.platform).filter(Boolean))).join(", ");
  const guidelines = campaign?.description || (offer.deliverables || []).map((d) => d.notes).filter(Boolean).join(", ");
  const payouts = deal.payouts || {};
  const activeSince = contract?.athleteSignedAt || offer.sentAt || deal.createdAt;
  const stripStatus = signed ? "Active deal" : deal.statusLabel;

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to={`/brand/campaigns/${deal.campaignId}`}>Back</BackLink>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-6 py-4">
        <span className="flex items-center gap-3">
          <span className="bm-h-s text-bm-ink">{campaign?.name}</span>
          <span className="h-4 w-px bg-bm-strong" />
          <span className={`bm-h-s ${signed ? "text-bm-positive" : "text-bm-muted"}`}>{stripStatus}</span>
        </span>
        <span className="bm-body-m text-bm-muted">
          {[brand?.company, fmtDateRange(offer.startDate || campaign?.startDate, offer.endDate || campaign?.endDate)].filter(Boolean).join(" · ")}
        </span>
      </div>

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Campaign tracker</Overline>
        <h1 className="bm-h-l text-bm-ink">
          {campaign?.name} · {athleteName}
        </h1>
        <p className="bm-body-l text-bm-muted">
          Athlete tracking · {signed ? "Active" : deal.statusLabel} since {fmtDate(activeSince)}
        </p>
      </div>

      {/* Deal progress */}
      <Card className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <h2 className="bm-h-m text-bm-ink">Deal Progress</h2>
          <StatusBadge status={deal.status} />
        </div>
        <div className="flex items-center pt-2">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = n < step;
            const current = n === step;
            const last = i === STEPS.length - 1;
            return (
              <div key={label} className={`relative flex items-center ${last ? "shrink-0" : "min-w-0 flex-1"}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bm-label-xs ${done ? "bg-bm-placeholder text-white" : current ? "bg-bm-ink text-white" : "bg-bm-mutedbg text-bm-tertiary"}`}>
                  {done ? "✓" : n}
                </span>
                {!last && <span className={`h-[2px] flex-1 ${done ? "bg-bm-placeholder" : current ? "bg-bm-ink" : "bg-bm-border"}`} />}
                <span className={`absolute left-[44px] top-[-20px] whitespace-nowrap ${current ? "bm-h-xs text-bm-ink" : "bm-label-xs text-bm-muted"}`}>
                  {n}. {label}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          {/* Contract management */}
          <Card className="flex flex-col gap-5">
            <h2 className="bm-h-m text-bm-ink">Contract Management</h2>
            {contract ? (
              <>
                <Overline>Signatures · {signatures} of 2 complete</Overline>
                <div className="flex flex-col gap-3 md:flex-row">
                  <Signature who="Brand" name={brand?.company || "Brand"} label="Signature pending" at={contract.brandSignedAt} />
                  <Signature who="Athlete" name={athleteName} label="Signature pending" at={contract.athleteSignedAt} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {!contract.brandSignedAt && deal.status === "contract_sent" && (
                    <Btn size="sm" onClick={() => act("contract-sign", {}, "Contract signed.")} disabled={!!busy}>
                      {busy === "contract-sign" ? "Signing..." : "Sign contract"}
                    </Btn>
                  )}
                  <Btn variant="secondary" size="sm" onClick={download}>
                    Download {contract.hasFile ? "file" : "PDF"}
                  </Btn>
                  {!signed && (
                    <>
                      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={upload} />
                      <button type="button" onClick={() => fileRef.current?.click()} className="bm-label-xs text-bm-ink hover:text-bm-orange">
                        Upload new contract
                      </button>
                    </>
                  )}
                  {contract.fileName && <span className="bm-body-s text-bm-muted">{contract.fileName}</span>}
                </div>
              </>
            ) : (
              <p className="bm-body-m text-bm-muted">
                {deal.status === "accepted"
                  ? "The athlete accepted. Send the contract from the campaign page to lock the deal."
                  : "A contract is issued once the athlete accepts the offer."}
              </p>
            )}
          </Card>

          {/* Deliverables */}
          <Card className="flex flex-col gap-4">
            <h2 className="bm-h-m text-bm-ink">Campaign Deliverables</h2>
            {signed && deal.deliverables?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead>
                    <tr>
                      <th className="bm-th">Deliverable</th>
                      <th className="bm-th">Platform</th>
                      <th className="bm-th">Due date</th>
                      <th className="bm-th">Status</th>
                      <th className="bm-th !text-right">Brand action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.deliverables.map((d) => {
                      const meta = DELIVERABLE_STATUS[d.status] || DELIVERABLE_STATUS.not_started;
                      const review = (d.submissions || []).find((s) => s.status === "under_review");
                      const doneLabel = d.status === "approved" ? "Done" : `${d.approvedCount || 0} of ${d.quantity || 1} Done`;
                      return (
                        <tr key={d.id} className="h-16">
                          <td className="bm-td bm-label-l text-bm-ink">{d.label}</td>
                          <td className="bm-td bm-body-m text-bm-muted">{d.platform}</td>
                          <td className="bm-td bm-body-m text-bm-muted">{fmtDate(d.dueDate, { year: false }) || EMPTY_VALUE}</td>
                          <td className="bm-td">
                            {d.status === "in_progress" || d.status === "approved" ? (
                              <Badge tone="passive">{doneLabel}</Badge>
                            ) : (
                              <Badge tone={meta.tone} dot={meta.dot}>{meta.label}</Badge>
                            )}
                          </td>
                          <td className="bm-td text-right">
                            {review ? (
                              <Btn variant="secondary" size="sm" onClick={() => navigate(`/brand/deals/${dealId}/content/${review.id}`)}>
                                Review content
                              </Btn>
                            ) : (
                              <Btn variant="secondary" size="sm" disabled>
                                {d.status === "locked" ? "Locked" : d.status === "approved" ? "Approved" : "Waiting"}
                              </Btn>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="bm-body-m text-bm-muted">
                  {signed ? "No deliverables were defined on this offer." : "Deliverables unlock once the contract is signed by both parties."}
                </p>
                {(offer.deliverableLines || []).length > 0 && (
                  <ul className="bm-body-m text-bm-ink">
                    {offer.deliverableLines.map((l) => (
                      <li key={l}>• {l}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>

          {/* Payouts */}
          <Card className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="bm-h-m text-bm-ink">Milestone Payouts</h2>
              <span className="bm-label-l text-bm-ink">Total Value: {fmtMoney(payouts.total || offer.compensation || 0)}</span>
            </div>
            {(signed ? deal.milestones : offer.milestones)?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead>
                    <tr>
                      <th className="bm-th">Milestone</th>
                      <th className="bm-th">Amount</th>
                      <th className="bm-th">Due date</th>
                      <th className="bm-th">Brand status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(signed ? deal.milestones : offer.milestones).map((m, i) => {
                      const meta = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.scheduled;
                      return (
                        <tr key={m.id || i} className="h-12">
                          <td className="bm-td bm-body-m capitalize text-bm-ink">{m.label}</td>
                          <td className="bm-td bm-num-m text-bm-ink">{fmtMoney(m.amount)}</td>
                          <td className="bm-td bm-body-m text-bm-muted">{fmtDate(m.dueDate, { year: false }) || EMPTY_VALUE}</td>
                          <td className="bm-td">
                            {m.status === "pending_approval" ? (
                              <button type="button" onClick={() => release(m)} disabled={!!busy} className="inline-flex">
                                <Badge tone="outline" dot={meta.dot}>{busy === `release-${m.id}` ? "Releasing..." : meta.label}</Badge>
                              </button>
                            ) : (
                              <Badge tone={meta.tone} dot={meta.dot}>{signed ? meta.label : "Scheduled"}</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="bm-body-m text-bm-muted">No payout schedule yet.</p>
            )}
            <div className="flex flex-wrap gap-6 pt-2">
              {[
                ["Total Released", payouts.released],
                ["Pending Approval", payouts.pending],
                ["Scheduled", payouts.scheduled],
                ["Total Contract", payouts.total || offer.compensation],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <span className="bm-body-s text-bm-muted">{label}</span>
                  <span className="bm-num-m text-bm-ink">{fmtMoney(value || 0)}</span>
                </div>
              ))}
            </div>
            {signed && deal.status !== "completed" && (deal.milestones || []).every((m) => m.status === "released") && (
              <Btn variant="secondary" size="sm" className="self-start" onClick={() => act("complete", {}, "Deal completed.")} disabled={!!busy}>
                Mark deal completed
              </Btn>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="flex w-full flex-col gap-6 lg:w-[420px]">
          <Card className="flex flex-col gap-4">
            <h2 className="bm-h-m text-bm-ink">Campaign Brief</h2>
            <Brief label="Campaign name" value={campaign?.name} />
            <Brief label="Athlete" value={athleteName} />
            <Brief label="CacheScore" value={deal.athlete?.cacheScore ? `${deal.athlete.cacheScore}${deal.athlete.visTier ? ` (${deal.athlete.visTier})` : ""}` : EMPTY_VALUE} />
            <Brief label="Athlete rate" value={offer.compensation ? fmtMoney(offer.compensation) : EMPTY_VALUE} />
            <Brief label="Platform" value={platforms} />
            <Brief label="Campaign Period" value={fmtDateRange(offer.startDate || campaign?.startDate, offer.endDate || campaign?.endDate)} />
            <Brief label="Content Guidelines" value={guidelines} />
          </Card>

          <Card className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <img src={iconInsight} alt="" className="h-4 w-4" />
              <Overline>Deal health insight</Overline>
            </div>
            <p className="bm-body-m text-bm-muted">
              {deal.health ||
                (deal.status === "offer_sent"
                  ? `Offer is open${deal.offerDaysLeft !== null && deal.offerDaysLeft !== undefined ? ` for ${deal.offerDaysLeft} more day${deal.offerDaysLeft === 1 ? "" : "s"}` : ""}. A follow-up lifts response rates when an offer sits unanswered.`
                  : deal.status === "countered"
                    ? "A counter is waiting for your review."
                    : deal.status === "accepted"
                      ? "Accepted. Send the contract to lock the deal."
                      : deal.status === "contract_sent"
                        ? `${signatures} of 2 signatures in. The deal activates once both sides sign.`
                        : "No health signal yet.")}
            </p>
            {deal.status === "countered" && (
              <Btn variant="secondary" size="sm" className="self-start" onClick={() => navigate(`/brand/deals/${dealId}/counter`)}>
                Review counter
              </Btn>
            )}
          </Card>

          <Card className="flex flex-col gap-3">
            <Overline>Deal activity log</Overline>
            {deal.activity?.length ? (
              <div className="flex flex-col gap-3">
                {deal.activity.map((a) => (
                  <div key={a.id} className="flex gap-3">
                    <span className="w-12 shrink-0 bm-body-s text-bm-muted">{fmtDate(a.at, { year: false })}</span>
                    <span className="bm-body-m text-bm-ink">{a.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="bm-body-m text-bm-muted">Nothing logged yet.</p>
            )}
          </Card>

          <Btn variant="ghost" className="self-start" onClick={message}>
            Message {deal.athlete?.name || "athlete"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default DealTrackingPage;
